import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart3, Calendar, Wallet, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkerMonthlySummary = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/worker/my-monthly-summary`, {
        params: { year, month }, withCredentials: true
      });
      setData(res.data);
    } catch (e) { /* silenced */ }
  }, [year, month]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) years.push(y);

  return (
    <Card className="border-[#E4E4E7] shadow-sm" data-testid="worker-monthly-summary-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#34C759]" />
          Riepilogo Mensile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Month/Year selectors */}
        <div className="flex gap-2">
          <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="flex-1" data-testid="worker-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="worker-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-center text-sm text-[#71717A] py-4">Caricamento...</p>
        ) : data ? (
          <div className="space-y-3">
            {/* Key stats */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#0055FF]/5 rounded-lg p-3 text-center">
                <Calendar className="w-4 h-4 text-[#0055FF] mx-auto mb-1" />
                <p className="text-xl font-bold text-[#0055FF]">{data.days_worked}</p>
                <p className="text-xs text-[#71717A]">Giorni lavorati</p>
                <p className="text-[10px] text-[#71717A]">su {data.working_days_in_month} lavorativi</p>
              </div>
              <div className="bg-[#34C759]/5 rounded-lg p-3 text-center">
                <Wallet className="w-4 h-4 text-[#34C759] mx-auto mb-1" />
                <p className="text-xl font-bold text-[#34C759]">€{data.compensation.toFixed(2)}</p>
                <p className="text-xs text-[#71717A]">Retribuzione</p>
                <p className="text-[10px] text-[#71717A]">€{data.daily_rate.toFixed(2)}/giorno</p>
              </div>
            </div>

            {/* Details table */}
            <div className="bg-[#F4F4F5] rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#71717A]">Busta Paga</span>
                <span className="font-medium text-[#09090B]">€{data.busta_paga.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#71717A]">Pagata</span>
                <span className={`font-medium ${data.busta_paga_paid ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                  {data.busta_paga_paid ? 'Si' : 'No'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#71717A]">Extra (Comp. - Busta)</span>
                <span className="font-medium text-[#09090B]">€{data.extra.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#71717A]">Acconti</span>
                <span className="font-medium text-[#09090B]">€{data.acconti_total.toFixed(2)}</span>
              </div>
              {data.extra_payments_total > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#71717A]">Pagamenti Extra</span>
                  <span className="font-medium text-[#FF9500]">€{data.extra_payments_total.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-[#E4E4E7] pt-2 flex justify-between text-sm">
                <span className="font-semibold text-[#09090B] flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Residuo
                </span>
                <span className={`font-bold ${data.residuo > 0 ? 'text-[#FF9500]' : data.residuo < 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                  €{data.residuo.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Extra payments detail */}
            {data.extra_payments && data.extra_payments.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-[#FF9500]">Pagamenti Extra:</p>
                {data.extra_payments.map((ep) => {
                  const typeLabels = { ferie: 'Ferie', giornate_extra: 'Giornate Extra', bonus: 'Bonus', altro: 'Altro' };
                  return (
                    <div key={ep.payment_id || `${ep.type}-${ep.amount}`} className="flex justify-between text-xs px-2 py-1 bg-[#FF9500]/5 rounded">
                      <span>{typeLabels[ep.type] || ep.type}{ep.note ? ` — ${ep.note}` : ''}</span>
                      <span className="font-medium">€{ep.amount.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-[#71717A] py-4">Nessun dato disponibile</p>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkerMonthlySummary;
